'use client';

import { useEffect, useRef, useState } from 'react';

/** Event, das `StudentLogin` bei erfolgreichem Login feuert (startet den Übergang). */
export const LOGIN_TRANSITION_EVENT = 'checkpoint:login-transition';
/** Event, das die erste authentifizierte Schüler-Fläche beim Mount feuert (`/heute`
 *  ODER `/checkin`, falls das Gate umleitet) → der Splash weiß, dass der echte
 *  Screen sichtbar ist, und blendet erst DANN aus (sauberer Übergang, kein Flash). */
export const POST_LOGIN_READY_EVENT = 'checkpoint:post-login-ready';

/** Mindestdauer des Brand-Moments (Login ist selten → bewusst zum Verweilen). Der
 *  Splash bleibt mindestens so lange UND bis /heute bereit ist. */
const MIN_DURATION_MS = 6000;
/** Sicherheitskappe, falls /heute nie „bereit" meldet — dann trotzdem ausblenden. */
const MAX_DURATION_MS = 14000;
const FADE_MS = 500;

/**
 * Login→/heute-Übergangs-Overlay — Brand-Moment NACH dem Login.
 *
 * Sitzt im ROOT-Layout, das die Soft-Navigation `/start → /heute` überlebt → EIN
 * durchgehendes Overlay über die Route-Grenze, ohne weißes Aufblitzen. Per
 * window-Event gestartet; blendet erst aus, wenn die Zielfläche (`/heute` ODER
 * `/checkin`, falls das Gate umleitet) `POST_LOGIN_READY_EVENT` meldet UND die
 * Mindestdauer erreicht ist (kein Login-Flash, sauberer Übergang). Sicherheits-
 * kappe: `MAX_DURATION_MS`.
 *
 * Anzeige: generiertes Bergsee-Vollbild + dezenter Indikator. `<picture>` wählt
 * orientierungs-spezifisch: Portrait (Handy) `/splash-bg-portrait.webp`, sonst das
 * Querformat `/splash-bg.webp` — beide voll-bleed (`object-cover`), 2×-WebP (scharf
 * auf Retina, klein). Bewusst `<img>`/`<picture>` (kein `next/image`) → kein
 * `<head>`-Preload, kein Hook-Fehler.
 */
export function LoginTransition() {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [heuteReady, setHeuteReady] = useState(false);
  const startRef = useRef(0);

  // Start- und „/heute-bereit"-Signale (Listener bleiben dauerhaft aktiv).
  useEffect(() => {
    const onStart = () => {
      setHeuteReady(false);
      setExiting(false);
      startRef.current = performance.now();
      setVisible(true);
    };
    const onReady = () => setHeuteReady(true);
    window.addEventListener(LOGIN_TRANSITION_EVENT, onStart);
    window.addEventListener(POST_LOGIN_READY_EVENT, onReady);
    return () => {
      window.removeEventListener(LOGIN_TRANSITION_EVENT, onStart);
      window.removeEventListener(POST_LOGIN_READY_EVENT, onReady);
    };
  }, []);

  // Ausblenden, sobald /heute bereit UND Mindestdauer erreicht — sonst Sicherheitskappe.
  useEffect(() => {
    if (!visible) return;
    const elapsed = performance.now() - startRef.current;
    const timers = [
      window.setTimeout(() => setExiting(true), Math.max(0, MAX_DURATION_MS - elapsed)),
    ];
    if (heuteReady) {
      timers.push(window.setTimeout(() => setExiting(true), Math.max(0, MIN_DURATION_MS - elapsed)));
    }
    return () => timers.forEach((t) => clearTimeout(t));
  }, [visible, heuteReady]);

  // Nach dem Fade vollständig unmounten.
  useEffect(() => {
    if (!exiting) return;
    const t = window.setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, FADE_MS + 50);
    return () => clearTimeout(t);
  }, [exiting]);

  if (!visible) return null;

  return (
    <div
      className={`${exiting ? 'splash-leave' : 'splash-enter'} fixed inset-0 z-[200] overflow-hidden bg-[#04211f]`}
      role="status"
      aria-live="polite"
      aria-label="Wird geladen"
    >
      {/* Generiertes Bergsee-Vollbild, orientierungs-spezifisch: Portrait-Screens
          (Handy) → Hochformat, sonst Querformat. Beide voll-bleed (object-cover). */}
      <picture>
        <source media="(orientation: portrait)" srcSet="/splash-bg-portrait.webp" />
        <img
          src="/splash-bg.webp"
          alt=""
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
      </picture>

      {/* sanfter Abdunkler unten — hält den Indikator immer lesbar */}
      <div className="absolute inset-x-0 bottom-0 h-44 bg-linear-to-t from-black/55 to-transparent" />

      {/* Indikator: orientierungs-spezifisch in eine ruhige Zone, damit er nicht auf
          dem Sockel/den Stufen liegt — Landscape unten-links (dunkle Ecke), Portrait
          zentriert unten. Breit + markant, durchlaufender Lime-Schimmer. */}
      <div className="splash-ind absolute h-1.5 w-[clamp(200px,46vw,380px)] overflow-hidden rounded-full bg-white/20 shadow-[0_2px_14px_rgba(0,0,0,0.45)] landscape:left-[6%] landscape:bottom-[12%] portrait:left-1/2 portrait:-translate-x-1/2 portrait:bottom-[6%]">
        <div className="splash-shimmer h-full w-2/5 rounded-full bg-linear-to-r from-transparent via-signal to-transparent" />
      </div>
    </div>
  );
}
